import {
  ApiError,
  getLocationTicketTypesConfig,
  updateLocationTicketTypesConfig,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { TicketTypesConfigResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Component ─────────────────────────────────────────────────────────────────

export function TicketTypesConfigPage() {
  const { t, i18n } = useTranslation('location-ticket-types-config');
  const { locationId } = useParams({ strict: false });

  const [config, setConfig] = useState<TicketTypesConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [qrEnabled, setQrEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const response = await getLocationTicketTypesConfig(locationId);
      setConfig(response);
      setSmsEnabled(response.smsEnabled);
      setQrEnabled(response.qrEnabled);
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  // Client-side guard: both disabled → save is blocked (mirrors 422 semantics)
  const saveDisabled = submitting || (!smsEnabled && !qrEnabled);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId || saveDisabled) return;
    setSubmitting(true);
    setErrorBanner(null);
    try {
      const saved = await updateLocationTicketTypesConfig(locationId, { smsEnabled, qrEnabled });
      setConfig(saved);
      setSmsEnabled(saved.smsEnabled);
      setQrEnabled(saved.qrEnabled);
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <fieldset className="space-y-4">
                <legend className="text-[13px] font-medium text-jq-graphite">
                  {t('form.enabledTypesLegend')}
                </legend>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="field-sms"
                    checked={smsEnabled}
                    onChange={(e) => setSmsEnabled(e.target.checked)}
                    className="h-4 w-4 rounded accent-jq-primary"
                  />
                  <label htmlFor="field-sms" className="text-[13px] text-jq-graphite">
                    {t('form.smsDigital')}
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="field-qr"
                    checked={qrEnabled}
                    onChange={(e) => setQrEnabled(e.target.checked)}
                    className="h-4 w-4 rounded accent-jq-primary"
                  />
                  <label htmlFor="field-qr" className="text-[13px] text-jq-graphite">
                    {t('form.qrPhysical')}
                  </label>
                </div>

                {!smsEnabled && !qrEnabled && (
                  <p className="text-[12px] text-jq-danger">{t('form.atLeastOneRequired')}</p>
                )}
              </fieldset>

              {config?.updatedAt && (
                <p className="text-[12px] text-jq-graphite/60">
                  {t('form.lastUpdated', { date: formatDateTime(config.updatedAt, i18n.language) })}
                </p>
              )}

              <Button type="submit" disabled={saveDisabled}>
                {submitting ? t('form.saving') : t('form.save')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-ticket-types-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
